/* ======================================
   Maison d'Etto v2 — <v2-chip-strip>
   The segmented control (WS 06): a strip of toggle buttons, built once and
   shared by the shop filters, the Journal filters and the Stockists page.
   - chips are <button type="button" data-chip="key" aria-pressed="…">;
     a chip with data-chip="all" is the reset and is pressed when nothing else is
   - data-mode="single": one pressed chip at a time (All = none);
     data-mode="multi": any number, All clears them all
   - arrow keys move focus between chips (roving tabindex), Home/End jump to the
     ends, Space/Enter toggle (native button behaviour)
   - a tap dispatches a bubbling 'v2:chips' event with
     detail { strip, chip, pressed, keys } where keys is the pressed set (no 'all')
   - strip.set(keys) sets the pressed state from outside without dispatching;
     strip.keys() reads it
   - phones: an optional [data-chips-toggle] button inside the strip turns it into
     a disclosure — the chips stack in [data-chips-list] under the trigger, which
     shows the pressed chips' labels in [data-chips-value]. Single mode closes
     after a choice, multi stays open for a second pick. Desktop ignores it (CSS).
   ====================================== */
(function () {
  'use strict';

  class V2ChipStrip extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode === 'multi' ? 'multi' : 'single';
      this.onClick = this.handleClick.bind(this);
      this.onKey = this.handleKey.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);
      this.toggle = this.querySelector('[data-chips-toggle]');
      this.valueEl = this.querySelector('[data-chips-value]');
      this.sync();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
    }

    get chips() {
      return [...this.querySelectorAll('[data-chip]')].filter(function (c) { return !c.hidden; });
    }

    keys() {
      return this.chips
        .filter(function (c) { return c.dataset.chip !== 'all' && c.getAttribute('aria-pressed') === 'true'; })
        .map(function (c) { return c.dataset.chip; });
    }

    find(key) {
      return this.chips.find(function (c) { return c.dataset.chip === key; }) || null;
    }

    /* set the pressed set from outside (URL restore, a sibling control) — no event */
    set(keys) {
      keys = keys || [];
      this.chips.forEach(function (c) {
        if (c.dataset.chip === 'all') return;
        c.setAttribute('aria-pressed', keys.indexOf(c.dataset.chip) !== -1 ? 'true' : 'false');
      });
      this.sync();
    }

    /* All mirrors the rest; roving tabindex lands on the first pressed chip */
    sync() {
      var pressed = this.keys();
      var all = this.find('all');
      if (all) all.setAttribute('aria-pressed', pressed.length ? 'false' : 'true');
      var focusable = this.chips.find(function (c) { return c.getAttribute('aria-pressed') === 'true'; }) || this.chips[0];
      this.chips.forEach(function (c) { c.tabIndex = c === focusable ? 0 : -1; });
      /* the disclosure trigger reads the selection: pressed labels, or All's label when nothing is */
      if (this.valueEl) {
        var labels = this.chips.filter(function (c) { return c.getAttribute('aria-pressed') === 'true'; })
          .map(function (c) { return c.textContent.trim(); });
        this.valueEl.textContent = labels.join(' | ');
      }
    }

    open(on) {
      if (!this.toggle) return;
      this.classList.toggle('is-open', on);
      this.toggle.setAttribute('aria-expanded', on ? 'true' : 'false');
    }

    handleClick(e) {
      var toggle = e.target.closest('[data-chips-toggle]');
      if (toggle && this.contains(toggle)) { e.preventDefault(); this.open(!this.classList.contains('is-open')); return; }
      var chip = e.target.closest('[data-chip]');
      if (!chip || !this.contains(chip)) return;
      e.preventDefault();
      var key = chip.dataset.chip;
      var pressed;
      if (key === 'all') {
        this.set([]);
        pressed = true;
      } else {
        pressed = chip.getAttribute('aria-pressed') !== 'true';
        if (this.mode === 'single') this.set(pressed ? [key] : []);
        else { chip.setAttribute('aria-pressed', pressed ? 'true' : 'false'); this.sync(); }
      }
      if (this.mode === 'single') this.open(false);
      this.dispatchEvent(new CustomEvent('v2:chips', { bubbles: true, detail: { strip: this, chip: chip, pressed: pressed, keys: this.keys() } }));
    }

    handleKey(e) {
      var chips = this.chips;
      var i = chips.indexOf(document.activeElement);
      if (i === -1) return;
      var next = null;
      switch (e.key) {
        case 'ArrowRight': case 'ArrowDown': next = chips[(i + 1) % chips.length]; break;
        case 'ArrowLeft': case 'ArrowUp': next = chips[(i - 1 + chips.length) % chips.length]; break;
        case 'Home': next = chips[0]; break;
        case 'End': next = chips[chips.length - 1]; break;
        default: return;
      }
      e.preventDefault();
      chips.forEach(function (c) { c.tabIndex = c === next ? 0 : -1; });
      next.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('v2-chip-strip')) {
    customElements.define('v2-chip-strip', V2ChipStrip);
  }
})();
